import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  CheckCircle, 
  AlertCircle, 
  ArrowLeft,
  Users,
  FileText,
  Edit,
  Upload,
  Receipt
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { contasReceberService, clientesService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';

const FinalizacaoContas = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Determinar o modo (criar, visualizar, editar)
  const isEditMode = window.location.pathname.includes('/editar');
  const isViewMode = id && !isEditMode;
  
  // Detectar cliente pré-selecionado na URL
  const urlParams = new URLSearchParams(location.search);
  const clientePreSelecionado = urlParams.get('cliente');
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    cliente: '',
    valor_pago: '',
    data_pagamento: '',
    forma_pagamento: 'PIX',
    tipo_baixa: 'Total',
    observacoes: '',
    contas_ids: [],
    comprovante: null,
    // Campos adicionais da API VHSYS
    valor_juros: '0',
    valor_desconto: '0',
    valor_acrescimo: '0',
    valor_taxa: '0',
    retorno_pagamento: '0',
    obs_pagamento: ''
  });

  // Estados da interface
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [showContasSelection, setShowContasSelection] = useState(false);

  // Buscar clientes com contas em aberto (otimizado)
  const { data: clientes = [], isLoading: loadingClientes, error: clientesError } = useQuery({
    queryKey: ['clientes-finalizacao'],
    queryFn: async () => {
      console.log('Buscando clientes com contas em aberto...');
      try {
        const response = await clientesService.comContasAbertas(1000);
        console.log('Resposta da API:', response);
        return response || [];
      } catch (error) {
        console.error('Erro ao buscar clientes:', error);
        throw error;
      }
    },
    staleTime: 300000, // 5 minutos
    cacheTime: 600000, // 10 minutos
    retry: 1,
    onSuccess: (data) => {
      console.log('Clientes com contas em aberto carregados com sucesso:', data);
    },
    onError: (error) => {
      console.error('Erro ao carregar clientes:', error);
    }
  });

  // Log para debug
  useEffect(() => {
    console.log('Estado da query de clientes:', {
      isLoading: loadingClientes,
      error: clientesError,
      data: clientes,
      dataLength: clientes?.length
    });
  }, [loadingClientes, clientesError, clientes]);

  // Função para limpar cache e refetch
  const handleRefresh = () => {
    console.log('Limpando cache e refazendo query...');
    queryClient.clear();
    queryClient.invalidateQueries(['clientes']);
  };

  // Buscar finalização existente (se estiver em modo de visualização/edição)
  const { data: finalizacaoExistente, isLoading: loadingFinalizacao } = useQuery({
    queryKey: ['finalizacao', id],
    queryFn: () => contasReceberService.getConta(id),
    enabled: !!id,
    staleTime: 300000, // 5 minutos
  });

  // Buscar contas do cliente selecionado
  const { data: contasCliente = [], isLoading: loadingContas } = useQuery({
    queryKey: ['contas-cliente-finalizacao', selectedCliente?.id],
    queryFn: () => {
      console.log('Buscando contas para cliente:', selectedCliente?.id);
      return selectedCliente ? clientesService.contasAbertas(selectedCliente.id) : Promise.resolve([]);
    },
    enabled: !!selectedCliente,
    staleTime: 60000, // 1 minuto
    onSuccess: (data) => {
      console.log('Contas carregadas:', data);
    },
    onError: (error) => {
      console.error('Erro ao carregar contas:', error);
    }
  });

  // Log para debug das contas
  useEffect(() => {
    console.log('Estado das contas:', {
      selectedCliente: selectedCliente?.id,
      showContasSelection,
      loadingContas,
      contasCliente: contasCliente?.length,
      contas_ids: formData.contas_ids?.length
    });
  }, [selectedCliente, showContasSelection, loadingContas, contasCliente, formData.contas_ids]);

  // Mutation para finalizar contas
  const finalizarMutation = useMutation({
    mutationFn: async (data) => {
      const promises = data.contas.map(contaId => {
        const conta = contasCliente.find(c => c.id_conta_rec === contaId);
        if (!conta) return Promise.resolve();
        
        return contasReceberService.baixarConta(contaId, {
          valor_pago: data.valor_pago,
          data_pagamento: data.data_pagamento,
          forma_pagamento: data.forma_pagamento,
          tipo_baixa: data.tipo_baixa,
          observacoes: data.observacoes,
          obs_pagamento: data.obs_pagamento,
          valor_juros: data.valor_juros,
          valor_desconto: data.valor_desconto,
          valor_acrescimo: data.valor_acrescimo,
          valor_taxa: data.valor_taxa,
          retorno_pagamento: data.retorno_pagamento,
          liquidado_rec: data.tipo_baixa === 'Total' ? 'Sim' : 'Par'
        });
      });
      
      return Promise.all(promises);
    },
    onSuccess: () => {
      toast.success('Contas finalizadas com sucesso!');
      queryClient.invalidateQueries(['contas-cliente-finalizacao']);
      queryClient.invalidateQueries(['clientes']);
      queryClient.invalidateQueries(['dashboard']);
      navigate('/finalizacao-contas');
    },
    onError: (error) => {
      toast.error(`Erro ao finalizar contas: ${error.message}`);
    }
  });

  const handleClienteChange = useCallback((clienteId) => {
    console.log('handleClienteChange chamado com ID:', clienteId, 'tipo:', typeof clienteId);
    const cliente = clientes.find(c => c.id === parseInt(clienteId));
    console.log('Cliente encontrado:', cliente);
    setSelectedCliente(cliente);
    setFormData(prev => ({ ...prev, cliente: clienteId, contas_ids: [] }));
    setShowContasSelection(true);
  }, [clientes]);

  // Efeito para carregar cliente pré-selecionado
  useEffect(() => {
    if (clientePreSelecionado && clientes.length > 0) {
      const cliente = clientes.find(c => c.id.toString() === clientePreSelecionado);
      if (cliente) {
        handleClienteChange(cliente.id);
      }
    }
  }, [clientePreSelecionado, clientes, handleClienteChange]);

  // Efeito para carregar dados da finalização existente
  useEffect(() => {
    if (finalizacaoExistente && isViewMode) {
      setFormData(prev => ({
        ...prev,
        ...finalizacaoExistente
      }));
    }
  }, [finalizacaoExistente, isViewMode]);

  const handleContaToggle = (contaId) => {
    setFormData(prev => ({
      ...prev,
      contas_ids: prev.contas_ids.includes(contaId)
        ? prev.contas_ids.filter(id => id !== contaId)
        : [...prev.contas_ids, contaId]
    }));
  };

  const handleSelectAllContas = () => {
    const allContaIds = contasCliente.map(conta => conta.id_conta_rec);
    setFormData(prev => ({
      ...prev,
      contas_ids: prev.contas_ids.length === allContaIds.length ? [] : allContaIds
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, comprovante: file }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.contas_ids.length === 0) {
      toast.error('Selecione pelo menos uma conta para finalizar');
      return;
    }

    if (!formData.valor_pago || !formData.data_pagamento) {
      toast.error('Preencha o valor pago e a data de pagamento');
      return;
    }

    finalizarMutation.mutate({
      ...formData,
      contas: formData.contas_ids
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calcular valor total das contas selecionadas
  const valorTotalSelecionado = formData.contas_ids.reduce((total, contaId) => {
    const conta = contasCliente.find(c => c.id_conta_rec === contaId);
    return total + (conta ? parseFloat(conta.valor_rec) : 0);
  }, 0);

  // Loading state
  if (loadingFinalizacao && isViewMode) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/finalizacao-contas')}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Carregando...</h1>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Carregando dados da finalização...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/finalizacao-contas')}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isEditMode ? 'Editar Finalização' : isViewMode ? 'Detalhes da Finalização' : 'Nova Finalização'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isEditMode 
                ? 'Edite os dados da finalização selecionada'
                : isViewMode 
                ? 'Visualize os detalhes da finalização'
                : 'Finalize contas a receber de um cliente'
              }
            </p>
          </div>
        </div>
        {isViewMode && (
          <button
            onClick={() => navigate(`/finalizacao-contas/${id}/editar`)}
            className="btn-primary flex items-center"
          >
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Seleção do Cliente */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2" />
            Seleção do Cliente
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cliente *
              </label>
              {loadingClientes ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Carregando clientes...</p>
                </div>
              ) : clientesError ? (
                <div className="text-center py-4">
                  <AlertCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                  <p className="text-sm text-red-600 dark:text-red-400">Erro ao carregar clientes</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{clientesError.message}</p>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="mt-2 btn-secondary text-sm"
                  >
                    Tentar novamente
                  </button>
                </div>
              ) : (
                <div className="flex space-x-2">
                  <select
                    value={formData.cliente}
                    onChange={(e) => handleClienteChange(e.target.value)}
                    className="input flex-1"
                    required
                    disabled={isViewMode}
                  >
                    <option value="">Selecione um cliente ({clientes.length} disponíveis)</option>
                    {clientes.map(cliente => (
                      <option key={cliente.id} value={cliente.id}>
                        {cliente.nome_cliente} - {cliente.total_contas_abertas} contas em aberto
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleRefresh}
                    className="btn-secondary px-3"
                    title="Atualizar lista de clientes"
                  >
                    ↻
                  </button>
                </div>
              )}

              {selectedCliente && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-blue-900">{selectedCliente.nome_cliente}</h3>
                      <p className="text-sm text-blue-700">
                        {selectedCliente.cpf_cnpj} • {selectedCliente.total_contas_abertas} contas em aberto • 
                        {formatCurrency(selectedCliente.total_valor_aberto || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Seleção de Contas */}
        {selectedCliente && showContasSelection && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Seleção de Contas
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Contas para Finalizar *
                </label>
                <button
                  type="button"
                  onClick={handleSelectAllContas}
                  className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  {formData.contas_ids.length === contasCliente.length ? 'Desmarcar todas' : 'Marcar todas'}
                </button>
              </div>

              {loadingContas ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Carregando contas...</p>
                </div>
              ) : contasCliente.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-md">
                  <CheckCircle className="mx-auto h-8 w-8 text-green-400 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma conta pendente para este cliente</p>
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                  {contasCliente.map((conta) => (
                    <div
                      key={conta.id_conta_rec}
                      className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={formData.contas_ids.includes(conta.id_conta_rec)}
                        onChange={() => handleContaToggle(conta.id_conta_rec)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                        disabled={isViewMode}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white">{conta.n_documento_rec}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          Vencimento: {formatDate(conta.vencimento_rec)} • 
                          Valor: {formatCurrency(conta.valor_rec)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.contas_ids.length > 0 && (
                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <p className="text-sm text-green-800 dark:text-green-200">
                    <span className="font-medium">{formData.contas_ids.length}</span> contas selecionadas • 
                    Valor total: <span className="font-medium">{formatCurrency(valorTotalSelecionado)}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dados da Finalização */}
        {formData.contas_ids.length > 0 && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Receipt className="h-5 w-5 mr-2" />
              Dados da Finalização
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Informações Básicas */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-900 dark:text-white">Informações Básicas</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Valor Pago *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor_pago}
                    onChange={(e) => handleInputChange('valor_pago', e.target.value)}
                    className="input w-full"
                    placeholder="0.00"
                    required
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Data de Pagamento *
                  </label>
                  <input
                    type="date"
                    value={formData.data_pagamento}
                    onChange={(e) => handleInputChange('data_pagamento', e.target.value)}
                    className="input w-full"
                    required
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Forma de Pagamento
                  </label>
                  <select
                    value={formData.forma_pagamento}
                    onChange={(e) => handleInputChange('forma_pagamento', e.target.value)}
                    className="input w-full"
                    disabled={isViewMode}
                  >
                    <option value="PIX">PIX</option>
                    <option value="Boleto">Boleto</option>
                    <option value="Cartão">Cartão</option>
                    <option value="Dinheiro">Dinheiro</option>
                    <option value="Transferência">Transferência</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Outros">Outros</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tipo de Baixa
                  </label>
                  <select
                    value={formData.tipo_baixa}
                    onChange={(e) => handleInputChange('tipo_baixa', e.target.value)}
                    className="input w-full"
                    disabled={isViewMode}
                  >
                    <option value="Total">Baixa Total</option>
                    <option value="Parcial">Baixa Parcial</option>
                  </select>
                </div>
              </div>

              {/* Valores Adicionais */}
              <div className="space-y-4">
                <h3 className="text-md font-medium text-gray-900 dark:text-white">Valores Adicionais</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Juros
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor_juros}
                    onChange={(e) => handleInputChange('valor_juros', e.target.value)}
                    className="input w-full"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Desconto
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor_desconto}
                    onChange={(e) => handleInputChange('valor_desconto', e.target.value)}
                    className="input w-full"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Acréscimo
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor_acrescimo}
                    onChange={(e) => handleInputChange('valor_acrescimo', e.target.value)}
                    className="input w-full"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Taxa
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.valor_taxa}
                    onChange={(e) => handleInputChange('valor_taxa', e.target.value)}
                    className="input w-full"
                    placeholder="0.00"
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </div>

            {/* Comprovante */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comprovante de Pagamento
              </label>
              <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600 dark:text-gray-400">
                    <label htmlFor="comprovante" className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                      <span>Fazer upload do comprovante</span>
                      <input
                        id="comprovante"
                        name="comprovante"
                        type="file"
                        accept="image/*,.pdf"
                        onChange={handleFileChange}
                        className="sr-only"
                        disabled={isViewMode}
                      />
                    </label>
                    <p className="pl-1">ou arraste e solte</p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PNG, JPG, PDF até 10MB
                  </p>
                  {formData.comprovante && (
                    <p className="text-sm text-green-600 dark:text-green-400">
                      ✓ {formData.comprovante.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Observações */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Observações do Pagamento
              </label>
              <textarea
                value={formData.obs_pagamento}
                onChange={(e) => handleInputChange('obs_pagamento', e.target.value)}
                rows="3"
                className="input w-full"
                placeholder="Observações sobre o pagamento..."
                disabled={isViewMode}
              />
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Observações Gerais
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
                rows="3"
                className="input w-full"
                placeholder="Observações gerais..."
                disabled={isViewMode}
              />
            </div>
          </div>
        )}

        {/* Botões */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/finalizacao-contas')}
            className="btn-secondary"
          >
            Cancelar
          </button>
          {!isViewMode && (
            <button
              type="submit"
              disabled={finalizarMutation.isLoading || formData.contas_ids.length === 0}
              className="btn-primary flex items-center"
            >
              {finalizarMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Finalizando...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isEditMode ? 'Atualizar Finalização' : 'Finalizar Contas'}
                </>
              )}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default FinalizacaoContas; 